/**
 * Vertex Analytics - Historial y Análisis de Sesiones
 * Muestra todas las sesiones completadas con métricas y evaluaciones
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  History, 
  MessageSquare, 
  TrendingUp, 
  Clock, 
  Award,
  ChevronRight,
  Loader2,
  BarChart3,
  Calendar,
  Filter,
  Users,
  Trash2
} from 'lucide-react';
import { getSafeToken } from '../../utils/tokenHelper';
import { VERTEX_API_BASE } from '../../utils/vertexApiHelper';

const VertexAnalytics = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [agents, setAgents] = useState([]);
  const [agentNameById, setAgentNameById] = useState({});
  const [selectedAgent, setSelectedAgent] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, completed, cancelled

  useEffect(() => {
    fetchAgentsDirectory();
    fetchSessions();
  }, []);

  useEffect(() => {
    // Recalcular opciones del combo cuando cambien sesiones o el directorio de agentes.
    if (!Array.isArray(sessions) || sessions.length === 0) {
      setAgents([]);
      return;
    }

    const hasDirectory = Object.keys(agentNameById || {}).length > 0;
    const uniqueAgentsMap = new Map();

    sessions.forEach((session) => {
      const agentId = session.agentConfig?.agentId || session.agentId || session.agent;
      if (!agentId) return;

      // Si tenemos directorio (fuente de verdad), SOLO mostramos agentes existentes ahí.
      if (hasDirectory && !agentNameById[agentId]) return;

      const nombreVisible = hasDirectory
        ? agentNameById[agentId]
        : (session.agentConfig?.nombreVisible || session.agentName || session.scenario || agentId);

      if (!uniqueAgentsMap.has(agentId)) {
        uniqueAgentsMap.set(agentId, {
          agentId,
          nombre: nombreVisible,
          avatar: session.agentConfig?.avatar || session.avatar || '🤖'
        });
      }
    });

    setAgents(Array.from(uniqueAgentsMap.values()));
  }, [sessions, agentNameById]);

  const fetchAgentsDirectory = async () => {
    try {
      const token = getSafeToken();
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };

      // Para el portal de usuario (rol student), este endpoint ya contempla assignedAgents y status=published.
      const response = await fetch(`${VERTEX_API_BASE}/agents`, { headers });
      const data = await response.json().catch(() => ({}));

      if (!data?.success) return;

      const list = Array.isArray(data?.agents) ? data.agents : [];
      const map = {};
      list.forEach((agent) => {
        const agentId = typeof agent?.agentId === 'string' ? agent.agentId : null;
        const nombreVisible = typeof agent?.nombreVisible === 'string' ? agent.nombreVisible.trim() : '';
        if (!agentId || !nombreVisible) return;
        map[agentId] = nombreVisible;
      });
      setAgentNameById(map);
    } catch (error) {
      console.error('❌ Error al cargar agentes (/v4/agents):', error);
    }
  };

  const getNombreVisible = (agentId, fallbackNombre) => {
    if (agentId && agentNameById[agentId]) return agentNameById[agentId];
    return fallbackNombre || agentId || 'Agente IA';
  };

  const handleDeleteSession = async (sessionId, e) => {
    e.stopPropagation(); // Evitar que se abra la sesión al hacer click en eliminar
    
    if (!confirm('¿Estás seguro de que quieres eliminar esta sesión? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      const token = getSafeToken();
      const response = await fetch(`${VERTEX_API_BASE}/v4/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      const data = await response.json();

      if (data.success) {
        // Eliminar de la lista local
        setSessions(prev => prev.filter(s => s.sessionId !== sessionId));
      } else {
        alert('Error al eliminar sesión: ' + data.message);
      }
    } catch (error) {
      console.error('Error al eliminar sesión:', error);
      alert('Error al eliminar la sesión');
    }
  };

  const fetchSessions = async () => {
    try {
      setIsLoading(true);
      setLoadError(null);
      const token = getSafeToken();

      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${VERTEX_API_BASE}/v4/sessions?limit=100`, {
        headers
      });

      const data = await response.json();
      console.log('📊 Respuesta del servidor:', data);
      console.log('📊 Status:', response.status);
      console.log('📊 Token usado:', token ? 'Presente' : 'No presente');

      if (data.success) {
        const sessionsList = data.sessions || [];
        setSessions(sessionsList);
        
        // Debug: Mostrar estructura de primera sesión
        if (sessionsList.length > 0) {
          console.log('🔍 Primera sesión completa:', sessionsList[0]);
          console.log('🔍 Campos disponibles:', Object.keys(sessionsList[0]));
        }

        // Las opciones del combo se recalculan por useEffect(sessions, agentNameById)
        console.log('🤖 Sesiones cargadas:', sessionsList.length);
        
        // Debug: Mostrar algunos ejemplos de sesiones con sus nombres  
        console.log('📝 Primeras 3 sesiones - estructura completa:');
        sessionsList.slice(0, 3).forEach((session, index) => {
          console.log(`  ${index + 1}. Sesión:`, session);
          console.log(`     AgentConfig:`, session.agentConfig);
          console.log(`     AgentId directo:`, session.agentId);
        });
        
        // Con populate automático del backend, los agentConfig ya vienen poblados
        console.log('✅ Usando datos con populate automático del backend');
        
        console.log('✅ Sesiones procesadas');
      } else {
        console.error('❌ Error en respuesta:', data.message);
        setSessions([]);
        setAgents([]);
        setLoadError(data.message || 'No se pudieron cargar las sesiones');
      }
    } catch (error) {
      console.error('❌ Error al cargar sesiones:', error);
      setSessions([]);
      setAgents([]);
      setLoadError('Error al cargar sesiones');
    } finally {
      setIsLoading(false);
    }
  };

  // Filtrar por agente seleccionado
  const sessionsByAgent = selectedAgent === 'all' 
    ? sessions 
    : sessions.filter(s => {
        const agentId = s.agentConfig?.agentId || s.agentId || s.agent;
        return agentId === selectedAgent;
      });

  // Filtrar por estado
  const filteredSessions = sessionsByAgent.filter(session => {
    const isCompleted = ['natural_completion', 'max_turns', 'coach_decision'].includes(session.endReason);
    const isCancelled = ['onboarding_cancelled', 'user_ended', 'moderator_ended'].includes(session.endReason);
    
    if (filter === 'completed') return isCompleted;
    if (filter === 'cancelled') return isCancelled;
    return true;
  });

  // Calcular métricas del agente seleccionado
  const stats = {
    total: sessionsByAgent.length,
    completed: sessionsByAgent.filter(s => 
      ['natural_completion', 'max_turns', 'coach_decision'].includes(s.endReason)
    ).length,
    avgScore: sessionsByAgent.length > 0
      ? sessionsByAgent
          .filter(s => s.finalEvaluation?.overallScore)
          .reduce((acc, s) => acc + s.finalEvaluation.overallScore, 0) /
          Math.max(1, sessionsByAgent.filter(s => s.finalEvaluation?.overallScore).length)
      : 0
  };

  const getStatusBadge = (endReason) => {
    const badges = {
      'natural_completion': { label: 'Completada', color: 'bg-green-100 text-green-700' },
      'max_turns': { label: 'Completada exitosamente', color: 'bg-green-100 text-green-700' },
      'coach_decision': { label: 'Completada (coach)', color: 'bg-green-100 text-green-700' },
      'user_ended': { label: 'Finalizada por usuario', color: 'bg-blue-100 text-blue-700' },
      'user_cancelled': { label: 'Cancelada por usuario', color: 'bg-yellow-100 text-yellow-700' },
      'onboarding_cancelled': { label: 'Cancelada', color: 'bg-gray-100 text-gray-600' },
      'moderator_ended': { label: 'Interrumpida', color: 'bg-red-100 text-red-700' }
    };
    return badges[endReason] || { label: endReason || 'Sin estado', color: 'bg-gray-100 text-gray-500' };
  };

  const getScoreColor = (score) => {
    if (score >= 8) return 'text-green-600';
    if (score >= 6) return 'text-blue-600';
    if (score >= 4) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-purple-50 via-white to-pink-50 overflow-y-auto">
      <div className="w-full min-h-min pb-20">
        <div className="w-full max-w-6xl mx-auto px-3 sm:px-6 lg:px-8 py-4 md:py-6">
        
        {/* Header con Selector de Agente */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 md:p-6 mb-4 md:mb-6 shadow-sm w-full">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-3 mb-1 md:mb-2">
                <BarChart3 className="w-6 h-6 md:w-8 md:h-8 text-purple-600 flex-shrink-0" />
                Análisis de Sesiones
              </h1>
              <p className="text-sm md:text-base text-gray-600">Historial completo de tus entrenamientos con agentes IA</p>
            </div>
            <button
              onClick={() => navigate('/vertex/simulator')}
              className="w-full md:w-auto justify-center px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition flex items-center gap-2"
            >
              <span>←</span> Volver
            </button>
          </div>

          {/* Selector de Agente */}
          <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 pt-4 border-t border-gray-200 w-full">
            <div className="flex items-center gap-2 text-gray-500">
              <Users className="w-5 h-5 flex-shrink-0" />
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Agente:</label>
            </div>
            <div className="w-full md:flex-1 min-w-0">
              <select
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white text-ellipsis overflow-hidden"
              >
                <option value="all">📊 Ver todos los agentes ({sessions.length})</option>
                {agents.map(agent => {
                  const count = sessions.filter(s => {
                    const agentId = s.agentConfig?.agentId || s.agentId || s.agent;
                    return agentId === agent.agentId;
                  }).length;
                  return (
                    <option key={agent.agentId} value={agent.agentId}>
                      {agent.avatar} {getNombreVisible(agent.agentId, agent.nombre)} ({count})
                    </option>
                  );
                })}
              </select>
              {loadError && (
                <p className="text-xs text-red-700 mt-2">{loadError}</p>
              )}
            </div>
          </div>
        </div>

        {/* Stats Cards - Dinámicas por agente seleccionado */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-6">
          <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-row sm:flex-col items-center sm:items-start justify-between gap-4">
            <div>
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1">Total Sesiones</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="p-2 bg-purple-50 rounded-lg">
              <History className="w-6 h-6 md:w-8 md:h-8 text-purple-600" />
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-row sm:flex-col items-center sm:items-start justify-between gap-4">
            <div>
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1">Completadas</p>
              <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
            </div>
            <div className="p-2 bg-green-50 rounded-lg">
              <Award className="w-6 h-6 md:w-8 md:h-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-row sm:flex-col items-center sm:items-start justify-between gap-4">
            <div>
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1">Promedio Score</p>
              <p className="text-2xl font-bold text-blue-600">
                {stats.avgScore > 0 ? stats.avgScore.toFixed(1) : '0.0'}
              </p>
            </div>
            <div className="p-2 bg-blue-50 rounded-lg">
              <TrendingUp className="w-6 h-6 md:w-8 md:h-8 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white border border-gray-200 rounded-xl p-3 md:p-4 mb-6 overflow-x-auto w-full">
          <div className="flex items-center gap-2 min-w-max">
            <Filter className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-700 mr-2">Filtrar:</span>
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
                filter === 'all'
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Todas
            </button>
            <button
              onClick={() => setFilter('completed')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
                filter === 'completed'
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Completadas
            </button>
            <button
              onClick={() => setFilter('cancelled')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
                filter === 'cancelled'
                  ? 'bg-gray-100 text-gray-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Canceladas
            </button>
          </div>
        </div>

        {/* Lista de Sesiones */}
        {isLoading ? (
          <div className="text-center py-20">
            <Loader2 className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-500">Cargando sesiones...</p>
          </div>
        ) : loadError ? (
          <div className="text-center py-20">
            <p className="text-gray-700 font-medium">No se pudieron cargar las sesiones</p>
            <p className="text-gray-500 text-sm mt-2">{loadError}</p>
          </div>
        ) : filteredSessions.length > 0 ? (
          <div className="space-y-4">
            {filteredSessions.map((session, index) => {
              const status = getStatusBadge(session.endReason);
              const hasScore = session.finalEvaluation?.overallScore !== null && 
                              session.finalEvaluation?.overallScore !== undefined;

              return (
                <div
                  key={session.sessionId}
                  className="bg-white border border-gray-200 rounded-xl p-4 md:p-5 hover:shadow-lg transition-all cursor-pointer group relative"
                  onClick={() => navigate(`/vertex/session/${session.sessionId}`)}
                >
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base md:text-lg font-semibold text-gray-900 group-hover:text-purple-600 transition-colors mb-1 pr-8 md:pr-0 break-words">
                        {session.scenarioContext?.titulo || `Sesión ${index + 1}`}
                      </h3>
                      <p className="text-sm text-gray-600 mb-1 break-words">
                        {(() => {
                          const agentId = session.agentConfig?.agentId || session.agentId || session.agent;
                          const fallback = session.agentConfig?.nombreVisible;
                          return getNombreVisible(agentId, fallback);
                        })()}
                      </p>
                      {session.userEmail && (
                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                          <Users className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="font-medium truncate">{session.userEmail}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between md:justify-end gap-3 w-full md:w-auto mt-2 md:mt-0 border-t md:border-t-0 border-gray-100 pt-3 md:pt-0">
                      {hasScore && (
                        <div className="flex items-center md:block gap-2">
                          <span className="text-xs text-gray-500 md:hidden">Calificación:</span>
                          <div className="text-right">
                            <div className={`text-2xl md:text-3xl font-bold ${getScoreColor(session.finalEvaluation.overallScore)}`}>
                              {session.finalEvaluation.overallScore.toFixed(1)}
                            </div>
                            <div className="text-xs text-gray-500 hidden md:block">/10</div>
                          </div>
                        </div>
                      )}
                      
                      <button
                        onClick={(e) => handleDeleteSession(session.sessionId, e)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors md:opacity-0 md:group-hover:opacity-100 opacity-100 absolute top-2 right-2 md:static"
                        title="Eliminar sesión"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs md:text-sm text-gray-600 mb-3">
                    <span className="flex items-center gap-1.5">
                      <Calendar className="w-4 h-4 flex-shrink-0" />
                      {new Date(session.createdAt).toLocaleDateString('es-ES', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>

                    <span className="flex items-center gap-1.5">
                      <MessageSquare className="w-4 h-4 flex-shrink-0" />
                      {session.turnCount} turnos
                    </span>

                    <span className="flex items-center gap-1.5">
                      <Clock className="w-4 h-4 flex-shrink-0" />
                      {(() => {
                        if (session.completedAt && session.createdAt) {
                          const durationMs = new Date(session.completedAt) - new Date(session.createdAt);
                          const minutes = Math.floor(durationMs / 60000);
                          const seconds = Math.floor((durationMs % 60000) / 1000);
                          return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
                        }
                        return 'N/A';
                      })()}
                    </span>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-gray-50">
                    <span className={`px-2.5 py-0.5 rounded-full text-[10px] md:text-xs font-medium ${status.color}`}>
                      {status.label}
                    </span>

                    <div className="flex items-center text-xs font-medium text-purple-600 group-hover:translate-x-1 transition-transform">
                      Ver detalles
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-20 bg-white border border-gray-200 rounded-xl">
            <History className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No hay sesiones {filter !== 'all' ? filteredSessions.length === 0 ? 'que coincidan con el filtro' : '' : 'aún'}
            </h3>
            <p className="text-gray-600 mb-4">
              Comienza tu primer entrenamiento con un agente IA
            </p>
            <button
              onClick={() => navigate('/vertex/simulator')}
              className="px-6 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
            >
              Ir al Simulador
            </button>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default VertexAnalytics;
